import {Device} from '@twilio/voice-sdk';
import {useCallback, useEffect, useMemo, useState} from 'react';

import {videoTokenApi} from 'api';
import {INVITEE_PARTICIPANT_ROLES} from 'constants/session';
import {useApiData} from 'hooks';
import {ParticipantRole} from 'types';

import {usePrevious} from './previous-value';

const TOKEN_EXPIRED_ERROR_CODE = 20104;

export function useTwilioDevice(role: ParticipantRole) {
    const [device, setDevice] = useState<Device>(null);
    const [token, setToken] = useState(null);
    const prevToken = usePrevious(token);

    const onTokenFetchSuccess = useCallback((data: {token: string}) => {
        setToken(data.token);
    }, []);

    const {send: getAgentToken} = useApiData(videoTokenApi.getAgentTwilioVoiceToken, {
        onSuccess: onTokenFetchSuccess,
    });

    const {send: getClientToken} = useApiData(videoTokenApi.getClientTwilioVoiceToken, {
        onSuccess: onTokenFetchSuccess,
    });

    const getToken = useMemo(() => INVITEE_PARTICIPANT_ROLES.includes(role) ?
        getClientToken : getAgentToken, [role, getClientToken, getAgentToken]);

    const init = useCallback(() => {
        if (!device) {
            getToken();
        }
    }, [device, getToken]);

    const handleDeviceError = useCallback((error: {code: number}) => {
        if (error?.code === TOKEN_EXPIRED_ERROR_CODE) {
            getToken();
        }
    }, [getToken]);

    useEffect(() => {
        if (!device && token) {
            const twilioDevice = new Device(token);
            setDevice(twilioDevice);
        }
    }, [token, device]);

    useEffect(() => {
        if (device && prevToken && token !== prevToken) {
            device.updateToken(token);
        }
    }, [token, prevToken, device]);

    useEffect(() => {
        if (device) {
            device.on('tokenWillExpire', getToken);
            device.on('error', handleDeviceError);
        }

        return () => {
            if (device) {
                device.off('tokenWillExpire', getToken);
                device.off('error', handleDeviceError);
            }
        };
    }, [device, getToken, handleDeviceError]);

    return {
        device,
        setDevice,
        init
    };

}